/**
 * Pacote `{ ttScl }` do SRG-SCL a partir do controle já carregado.
 * A API de produção (administrator-parleite) não tem /milkControls/importJson.
 */

const REASON_CODE: Record<string, number> = {
    Cio: 14,
    IN_HEAT: 14,
    Mastite: 22,
    MASTITIS: 22,
    'Recém Parida': 30,
    NEW_BORN: 30,
    'Em Tratamento': 26,
    IN_TREATMENT: 26,
    Exposição: 18,
    EXPOSITION: 18,
    'Descarte/Morte': 68,
    DISCARD_OR_DEATH: 68
};

interface MilkedLike {
    animal?: { id?: string; nickname?: string; register?: string } | null;
    production?: number | string | null;
    flaskNumber?: number | string | null;
    lot?: string | null;
    milkingNumber?: number | null;
    missingProduction?: unknown;
}

interface MilkingLike {
    startedAt?: Date | string | null;
    finishedAt?: Date | string | null;
    milkingNumber?: number | null;
    milkedAnimals?: MilkedLike[] | null;
}

export interface MilkControlLike {
    id?: string;
    startedAt?: Date | string | null;
    producer?: { code?: string | null } | null;
    property?: { apcId?: string | null } | null;
    controller?: { code?: string | null } | null;
    milkings?: unknown;
}

export function milkControlToImportPackage(control: MilkControlLike, producerCode?: string | null): { ttScl: Record<string, unknown>[] } {
    const milkings = Array.isArray(control?.milkings) ? (control.milkings as MilkingLike[]) : [];
    const byAnimal = new Map<string, Record<string, unknown>>();

    milkings.forEach((milking, index) => {
        const fallbackOrder = Number(milking.milkingNumber) || index + 1;
        (milking.milkedAnimals || []).forEach((milked) => {
            const animalId = milked?.animal?.id || milked?.animal?.nickname;
            if (!animalId) return;
            const order = Number(milked.milkingNumber) || fallbackOrder;
            if (!byAnimal.has(animalId)) {
                byAnimal.set(animalId, {
                    uiId: control.id,
                    codCliente: intOrNull(control.producer?.code || producerCode),
                    codPropriedade: intOrNull(control.property?.apcId),
                    codClienteSCL: intOrNull(control.producer?.code || producerCode),
                    codPropriedadeSCL: intOrNull(control.property?.apcId),
                    cControlador: intOrNull(control.controller?.code),
                    dataUltimoControle: dateStr(control.startedAt),
                    uiIdAnimal: milked.animal?.id || animalId,
                    registroAnimal: milked.animal?.register || null,
                    apelido: milked.animal?.nickname || null,
                    numeroFrasco: null,
                    producaoLeitePrimeiraOrdenha: null,
                    producaoLeitesegundaOrdenha: null,
                    producaoLeiteTerceiraOrdenha: null,
                    horaInicioOrdenha1: timeStr(milkingAt(milkings, 1)?.startedAt),
                    horaFinalOrdenha1: timeStr(milkingAt(milkings, 1)?.finishedAt),
                    horaInicioOrdenha2: timeStr(milkingAt(milkings, 2)?.startedAt),
                    horaFinalOrdenha2: timeStr(milkingAt(milkings, 2)?.finishedAt),
                    horaInicioOrdenha3: timeStr(milkingAt(milkings, 3)?.startedAt),
                    horaFinalOrdenha3: timeStr(milkingAt(milkings, 3)?.finishedAt),
                    loteAnimal: null,
                    oFicial: null,
                    vAplicativo: 'SIM',
                    ocorrenciaControle: null
                });
            }
            const row = byAnimal.get(animalId);
            const volumeKey =
                order === 1
                    ? 'producaoLeitePrimeiraOrdenha'
                    : order === 2
                      ? 'producaoLeitesegundaOrdenha'
                      : order === 3
                        ? 'producaoLeiteTerceiraOrdenha'
                        : null;
            if (volumeKey && row[volumeKey] == null) {
                row[volumeKey] = volumeStr(milked.production);
            }
            if (milked.flaskNumber != null && milked.flaskNumber !== '' && row.numeroFrasco == null) {
                row.numeroFrasco = String(milked.flaskNumber);
            }
            if (milked.lot && !row.loteAnimal) {
                row.loteAnimal = String(milked.lot);
            }
            const code = reasonCode(milked.missingProduction);
            if (code != null && row.ocorrenciaControle == null) {
                row.ocorrenciaControle = code;
            }
        });
    });

    const ttScl = Array.from(byAnimal.values()).sort((a, b) =>
        String(a.apelido || '').localeCompare(String(b.apelido || ''), 'pt-BR', { numeric: true })
    );
    return { ttScl };
}

function milkingAt(milkings: MilkingLike[], order: number): MilkingLike | null {
    return milkings.find((milking) => Number(milking.milkingNumber) === order) || milkings[order - 1] || null;
}

function intOrNull(value: unknown): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

function volumeStr(value: unknown): string | null {
    if (value == null || value === '') return null;
    const parsed = Number(String(value).replace(',', '.'));
    return Number.isFinite(parsed) ? String(parsed) : null;
}

function reasonCode(value: unknown): number | null {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && REASON_CODE[String(value)] == null && value > 0) return value;
    if (typeof value === 'string') return REASON_CODE[value] ?? null;
    if (typeof value === 'object') {
        const record = value as { code?: unknown; description?: unknown; name?: unknown };
        return reasonCode(record.code) ?? reasonCode(record.description) ?? reasonCode(record.name);
    }
    return null;
}

function dateStr(value: Date | string | null | undefined): string | null {
    const date = asDate(value);
    if (!date) return null;
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(date);
    return parts;
}

function timeStr(value: Date | string | null | undefined): string | null {
    const date = asDate(value);
    if (!date) return null;
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
    }).formatToParts(date);
    const pick = (type: string) => parts.find((part) => part.type === type)?.value;
    const hour = pick('hour');
    const minute = pick('minute');
    const second = pick('second');
    if (!hour || !minute) return null;
    return second && second !== '00' ? `${hour}:${minute}:${second}` : `${hour}:${minute}`;
}

function asDate(value: Date | string | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
